import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { StorageService } from '../storage/storage.service';
import { Language } from '../../models/language.model';
import { AppConfig } from 'src/app/app.config';
import { EventBusService, EventTopic } from '../event-bus/event-bus.service';

export interface Locale {
  name: string;
  languageFile: string;
  localName?: string;
  id: string;
  code: string;
  active: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class TranslateConfigService {
  public locales: Locale[] = [
    { name: 'Arabic', id: 'ar', code: 'sa', active: true, localName: 'اللغة العربية', languageFile: 'Arabic' },
    { name: 'English', id: 'en', code: 'us', active: true, localName: 'English', languageFile: 'English' },
    { name: 'German', id: 'de', code: 'de', active: true, localName: 'Deutsch', languageFile: 'German' },
    { name: 'Greek', id: 'el', code: 'gr', active: true, localName: 'Ελληνικά', languageFile: 'Greek' },
    { name: 'Hindi', id: 'hi', code: 'in', active: true, localName: 'हिंदी', languageFile: 'Hindi' },
    { name: 'Italian', id: 'it', code: 'it', active: true, localName: 'Italiano', languageFile: 'Italian' },
    { name: 'Latvian', id: 'lv', code: 'lv', active: true, localName: 'Latviešu valoda', languageFile: 'Latvian' },
    { name: 'Simplified Chinese', id: 'zh_cn', code: 'zh-cn', active: true, localName: '简体中文', languageFile: 'Chinese-Simplified' },
    { name: 'Portuguese', id: 'pt', code: 'pt', active: true, localName: 'Português', languageFile: 'Portuguese' },
    { name: 'Russian', id: 'ru', code: 'ru', active: true, localName: 'Русский', languageFile: 'Russian' },
    { name: 'Spanish', id: 'es', code: 'es', active: true, localName: 'Español', languageFile: 'Spanish' },
    { name: 'Vietnamese', id: 'vi', code: 'vn', active: true, localName: 'Tiếng Việt', languageFile: 'Vietnamese' },
    { name: 'Punjabi', id: 'pa', code: 'pa', active: true, localName: 'ਪੰਜਾਬੀ', languageFile: 'Punjabi' },
    { name: 'Burmese', id: 'my', code: 'my', active: true, localName: 'ဗမာစကား', languageFile: 'Burmese' },
  ];

  constructor(
    private translate: TranslateService,
    private storageSvc: StorageService,
    @Inject(AppConfig) private config: AppConfig,
    private eventBusSvc: EventBusService
  ) {
    this.refreshActiveLanguages();
    this.translate.onLangChange.subscribe(event => {
      const locale = this.locales.find(l => l.languageFile === event.lang);
      this.eventBusSvc.publish(EventTopic.CurrentLocaleUpdated, locale.id);
    });
    this.importDayJSLocales();
  }

  async setLanguage(id: string, save?: boolean) {
    if (save) await this.storageSvc.set('language', id);
    const locale = this.locales.find(l => l.id === id);
    this.translate.use(locale.languageFile);
  }

  async resetLanguage() {
    await this.storageSvc.remove('language');
  }

  async getLanguage(profileLanguage: Language | null = null, tenantLanguage: Language | null = null): Promise<string> {
    // Check profile language
    if (profileLanguage) return profileLanguage.locale;

    // Check storage language
    const language = await this.storageSvc.get('language');
    if (language) {
      if (language === 'zh-CN' || language === 'zh_cn') {
        return 'zh_cn';
      }
      return language;
    }

    // Get device browser lang and check whether we support it, if yes set it as default
    let browserLang = this.translate.getBrowserLang();
    if (browserLang) {
      if (browserLang === 'zh' || browserLang.toLowerCase() === 'zh-cn') {
        browserLang = 'zh_cn';
      }
      const supportedBrowserLang = this.getActiveLanguages().find(l => l.id === browserLang)?.id;
      if (supportedBrowserLang) return supportedBrowserLang;
    }

    // Check tenant language
    if (tenantLanguage) return tenantLanguage.locale;

    return 'en';
  }

  getActiveLanguages(): Array<Locale> {
    return this.locales.filter(locale => locale.active);
  }

  refreshActiveLanguages() {
    if (!Array.isArray(this.config.SUPPORTED_LANGUAGES) || this.config.SUPPORTED_LANGUAGES.length === 0) return;

    this.locales.forEach(locale => {
      locale.active = false;
      this.config.SUPPORTED_LANGUAGES.forEach(supportedLocale => {
        if (locale.id === supportedLocale) locale.active = true;
      });
    });
  }

  importDayJSLocales() {
    this.getActiveLanguages().forEach(locale => {
      let dayjsLocale = locale.id.toLowerCase();
      if (locale.id === 'zh_cn') {
        dayjsLocale = 'zh-cn';
      }
      import(`dayjs/locale/${dayjsLocale}`).catch(() => {
        // eslint-disable-next-line
        console.debug(`dayjs is not supporting ${locale.name} (${dayjsLocale})`);
      });
    });
  }
}
